import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { defaultBoundaryTerritoryGroupResponseSettings } from '~/boundary/settings/defaultBoundarySettings';
import { boundaryTerritoryGroupCreateSettingsStyleDefaults } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import { useCustomBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  boundaryTerritoryGroupsCreateError, boundaryTerritoryGroupsCreateRequest, boundaryTerritoryGroupsFetchRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  BoundaryMatchingType, getBoundaryTerritoryGroups,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { translate } from '~/translations/Trans';
import { type BoundaryFillType } from '../../../../../../boundary/settings/boundaryFill.type';
import { getCalculateBucketFunction } from '../../boundaryTerritoryGroup.helpers';
import { useBoundaryGroupUsage } from '../../hooks/useBoundaryGroupUsage';
import {
  type LoadCustomBoundaryGroupData,
  LoadCustomBoundaryGroupPaneComponent,
} from './loadCustomBoundaryGroupPane.component';
import { LoadCustomBoundaryFillType } from './loadCustomBoundaryGroupPane.helpers';

export type LoadCustomBoundaryGroupPaneContainerProps = Readonly<{
  resetForm: () => void;
}>;

export const LoadCustomBoundaryGroupPaneContainer: FC<LoadCustomBoundaryGroupPaneContainerProps> = props => {
  const { isBoundaryGroupUsedOnMap } = useBoundaryGroupUsage();
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();

  const handleAddBoundary = async (data: LoadCustomBoundaryGroupData) => {
    const territoryGroup = customBoundaryGroups.find(item => item.id === data.boundaryGroupId);

    if ((mapId === null) || (clientId === null) || !territoryGroup) {
      return;
    }

    const boundaryFillType: BoundaryFillType =
      data.boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData
        ? LoadCustomBoundaryFillType.DefaultFill :
        data.boundaryFillType;

    let boundaryTerritoryType = BoundaryTerritoryType.Manual;
    let calculateBucketFunction = getCalculateBucketFunction(BoundaryTerritoryType.Manual);
    let bucketColumnId = null;

    if (data.boundaryFillType === LoadCustomBoundaryFillType.FillBasedOnData) {
      boundaryTerritoryType = data.boundaryTerritoryType;
      calculateBucketFunction = getCalculateBucketFunction(data.boundaryTerritoryType, data.numericCalculateBucketFunction);
      bucketColumnId = data.boundaryFillData?.columnId ?? null;
    }

    const { boundary_territory_groups: serverBTGs } = await getBoundaryTerritoryGroups(clientId, { map_id: mapId });
    // BTG was potentially already added to the map
    const lookedUpGroup = serverBTGs.find(btg => btg.boundary_group_id === data.boundaryGroupId);

    // check if it was already added
    if (lookedUpGroup) {
      dispatch(boundaryTerritoryGroupsCreateError());

      dispatch(createAppError({
        type: AppErrorType.General,
        title: translate('Territory group was already added'),
        errorTitle: translate('Territory group was already added to the map'),
      }));

      dispatch(boundaryTerritoryGroupsFetchRequest(mapId));

      return;
    }

    dispatch(boundaryTerritoryGroupsCreateRequest({
      boundary_group_id: territoryGroup.id,
      map_id: mapId,
      matchings: {
        matching_type: BoundaryMatchingType.Geometry,
      },
      settings: {
        ...defaultBoundaryTerritoryGroupResponseSettings,
        bucket_column_id: bucketColumnId,
        boundary_fill_type: boundaryFillType,
        boundary_territories: [],
        boundary_territory_type: boundaryTerritoryType,
        style: boundaryTerritoryGroupCreateSettingsStyleDefaults,
        calculate_bucket_function: calculateBucketFunction,
      },
    }));

    props.resetForm();
  };

  return (
    <LoadCustomBoundaryGroupPaneComponent
      customBoundaryGroups={customBoundaryGroups}
      isBoundaryGroupUsedOnMap={isBoundaryGroupUsedOnMap}
      onAddBoundary={handleAddBoundary}
    />
  );
};
